import { useEffect, useMemo, useState } from 'react';
import { Button, Icon, Pagination, PaginationProps, Select } from '@gravity-ui/uikit';
import { MRT_ColumnFiltersState, MRT_SortingState } from 'material-react-table';
import { CaretsExpandVertical } from '@gravity-ui/icons';
import styles from './styles.module.scss';
import { AdminListBlock } from './admin-list-block';
import { CustomEstablishmentsFilter } from '../custom-establishment-filter';
import { CustomEstablishmentsSheetFilter } from '../sheets/custom-establishment-filter-sheet';
import { AccountBlockFilterSheet } from '../sheets/account-filter-sheet';

interface PersonProps {
	data: Person[];
	onAdd: () => void;
	pageSize: number;
	setPageSize: React.Dispatch<React.SetStateAction<number>>;
	pageIndex: number;
	setPageIndex: React.Dispatch<React.SetStateAction<number>>;
	totalItems: number | undefined;
	sorting: MRT_SortingState;
	setSorting: React.Dispatch<React.SetStateAction<MRT_SortingState>>;
	columnFilters: MRT_ColumnFiltersState;
	setColumnFilters: React.Dispatch<React.SetStateAction<MRT_ColumnFiltersState>>;
	selectedFilters: string[];
	isMobile: any;
	resetFilters: () => void;
}

interface Person {
	id: number;
	email: string;
	role: string;
	firstName: string;
	middleName: string;
	lastName: string;
	establishment: {
		id?: number;
		name?: string | null;
	} | null;
	imageUrl: string;
	region: {
		code?: number;
		fullname: string;
		guid?: string;
		name?: string;
	};
	specialty: string;
	active: boolean;
	createdDate: string;
}

export const AdminTableList = ({
	data,
	onAdd,
	pageSize,
	setPageSize,
	pageIndex,
	setPageIndex,
	totalItems,
	setSorting,
	setColumnFilters,
	selectedFilters,
	isMobile,
	resetFilters,
}: PersonProps) => {
	const [selectedValues, setSelectedValues] = useState<string[]>(['По дате создания']);

	const [active, setActive] = useState<Person[]>(data);

	const [visibleFilter, setVisibleFilter] = useState(false);

	useEffect(() => {
		setActive(data);
	}, [data]);

	const getUpdatedColumnFilters = (
		prevFilters: MRT_ColumnFiltersState,
		selected: string[],
	): MRT_ColumnFiltersState => {
		const withoutOldFilter = prevFilters.filter((f) => f.id !== 'establishment.name');

		return selected.length
			? [...withoutOldFilter, { id: 'establishment.name', value: selected }]
			: withoutOldFilter;
	};

	const handleSortChange = (value: string) => {
		switch (value) {
			case 'По дате создания':
				setSorting([{ id: 'createdDate', desc: false }]);
				break;
			case 'По ФИО специалиста От А-Я':
				{
					setSorting([{ id: 'lastName', desc: false }]);
				}

				break;
			case ' По ФИО специалиста От Я-А':
				{
					setSorting([{ id: 'lastName', desc: true }]);
				}
				break;
			case 'Сначала новые':
				setSorting([{ id: 'createdDate', desc: true }]);
				break;
			default:
				setSorting([{ id: 'createdDate', desc: true }]);
				break;
		}
	};

	const handlePaginationUpdate: PaginationProps['onUpdate'] = (newPage, newPageSize) => {
		setPageIndex(newPage - 1);
		setPageSize(newPageSize);
	};

	const onUpdate = (values: string[]) => {
		setSelectedValues(values);

		if (values.length > 0) {
			handleSortChange(values[0]);
		}
	};

	const updateAccountStatus = (updatedAccount: Person) => {
		setActive((prevData) =>
			prevData.map((account) => (account.id === updatedAccount.id ? updatedAccount : account)),
		);
	};

	const label = selectedValues && selectedValues.length > 0 ? selectedValues[0] : 'Не выбрано';

	return (
		<div className={styles.list_container}>
			<div className={styles.list_container__filter_block}>
				{isMobile ? (
					<>
						<Button view="flat" onClick={() => setVisibleFilter(true)}>
							<Icon size={16} data={CaretsExpandVertical} />
							{label}
						</Button>
						<AccountBlockFilterSheet
							visibleFilter={visibleFilter}
							setVisibleFilter={setVisibleFilter}
							onSortChange={(value: any) => {
								handleSortChange(value);
								setSelectedValues([value]);
							}}
						/>
					</>
				) : (
					<Select
						value={selectedValues}
						onUpdate={onUpdate}
						filterable={true}
						renderControl={({ onClick, onKeyDown, ref }) => {
							return (
								<Button ref={ref} onClick={onClick} extraProps={{ onKeyDown }} view="flat">
									<Icon size={16} data={CaretsExpandVertical} />
									<span>{label}</span>
								</Button>
							);
						}}
					>
						<Select.Option value="По дате создания">По дате создания</Select.Option>
						<Select.Option value="По ФИО специалиста От А-Я">По ФИО специалиста От А-Я</Select.Option>
						<Select.Option value="По ФИО специалиста От Я-А">По ФИО специалиста От Я-А</Select.Option>
						<Select.Option value="Сначала новыe">Сначала новые</Select.Option>
					</Select>
				)}

				{isMobile ? (
					<CustomEstablishmentsSheetFilter
						resetFilters={resetFilters}
						selectedFilters={selectedFilters}
						onApplyFilters={(selected: string[]) => {
							setColumnFilters((prev) => getUpdatedColumnFilters(prev, selected));
						}}
					/>
				) : (
					<CustomEstablishmentsFilter
						selectedFilters={selectedFilters}
						onApplyFilters={(selected: string[]) => {
							setColumnFilters((prev) => getUpdatedColumnFilters(prev, selected));
						}}
					/>
				)}
			</div>

			<div className={styles.list_container__blocks}>
				{active.map((account) => {
					return (
						<AdminListBlock
							account={account}
							onAdd={onAdd}
							updateAccountStatus={updateAccountStatus}
							isMobile={isMobile}
						/>
					);
				})}
			</div>

			<div className={styles.list_container__pagination}>
				{isMobile ? (
					<Pagination
						page={pageIndex + 1}
						pageSize={pageSize}
						total={totalItems}
						onUpdate={handlePaginationUpdate}
					/>
				) : (
					<Pagination
						page={pageIndex + 1}
						pageSize={pageSize}
						total={totalItems}
						pageSizeOptions={[10, 20, 40, 100]}
						onUpdate={handlePaginationUpdate}
					/>
				)}
			</div>
		</div>
	);
};
